/**
 * Analytics for promobile.app.
 *
 * All analytics is routed through a single GTM container (GTM-5KH9546N) plus a
 * Yandex.Metrika counter. Product events are pushed to `window.dataLayer`;
 * GA mapping is configured on the GTM side, not here.
 *
 * Nothing loads outside production. `react-scripts build` always sets
 * `NODE_ENV=production` (both `yarn build` and the staging `build:dev`), so
 * NODE_ENV alone can't tell prod from the EC2/staging stands — but that's what
 * the host allowlist below is for: only client.promobile.app matches. NODE_ENV
 * keeps analytics off during `yarn start` (NODE_ENV=development). We deliberately
 * do NOT use a REACT_APP_ENV flag: this project never defines it, so keying on it
 * left `isProduction` permanently false and silently disabled all analytics.
 *
 * Page views (including SPA route changes) are counted by the GTM container
 * automatically — we never push `page_view` by hand. Yandex.Metrika, however,
 * does need an explicit `hit` on every SPA route change (see trackYandexHit).
 */

const GTM_ID = "GTM-5KH9546N";
const YANDEX_COUNTER_ID = 97941847;

const isProduction = process.env.NODE_ENV === "production";

/**
 * Primary host guard. Since NODE_ENV=production is true on every build (prod and
 * the staging `build:dev`), the hostname is what actually distinguishes prod:
 * only client.promobile.app matches, so EC2/IP dev stands and dev subdomains
 * like platform-dev.promobile.app never load analytics. The marketing landing at
 * promobile.app is a separate site. Add hosts here to extend at once.
 */
const PROD_HOSTS = ["client.promobile.app"];
const isProdHost =
  typeof window !== "undefined" &&
  PROD_HOSTS.includes(window.location.hostname);

/**
 * Single source of truth for "trackers may load here". The cookie banner is pure
 * UI and renders everywhere; this gates only the tracker wiring — consent
 * signals to gtag and the direct Yandex.Metrika injection — so nothing tracks
 * outside client.promobile.app, dev/staging, or the marketing landing.
 */
export const isAnalyticsEnabled = (): boolean => isProduction && isProdHost;

let isInitialized = false;

const ensureDataLayer = (): void => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Google Consent Mode v2 gtag shim. GTM's own snippet normally defines this, but
 * here GTM is injected from JS (injectGtm), so we define the shim ourselves and
 * seed a `consent: default` = denied BEFORE the container loads. This is set from
 * code (not index.html) on purpose: the nginx-cached index.html would otherwise
 * serve a stale page without the default and let tags fire un-gated.
 *
 * Consent Mode only governs Google tags loaded through GTM. Yandex.Metrika is
 * injected directly (injectYandexMetrika) and is therefore gated in code instead
 * — see grantAnalyticsConsent, called by the cookie banner on acceptance.
 */
const setConsentDefault = (): void => {
  ensureDataLayer();
  window.gtag =
    window.gtag ||
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments as unknown as Record<string, any>);
    };
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
};

const injectGtm = (): void => {
  ensureDataLayer();
  window.dataLayer?.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

const injectYandexMetrika = (): void => {
  // Standard Yandex.Metrika counter snippet, ported to TS.
  /* eslint-disable */
  (function (m: any, e: Document, t: string, r: string, i: string) {
    m[i] =
      m[i] ||
      function () {
        (m[i].a = m[i].a || []).push(arguments);
      };
    m[i].l = 1 * (new Date() as any);
    for (let j = 0; j < e.scripts.length; j++) {
      if (e.scripts[j].src === r) {
        return;
      }
    }
    const k = e.createElement(t) as HTMLScriptElement;
    const a = e.getElementsByTagName(t)[0];
    k.async = true;
    k.src = r;
    a.parentNode?.insertBefore(k, a);
  })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
  /* eslint-enable */

  window.ym?.(YANDEX_COUNTER_ID, "init", {
    webvisor: true,
    clickmap: true,
    referrer: document.referrer,
    url: window.location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  });
};

/**
 * Boots GTM (with a denied Consent Mode default). No-op outside production, so
 * dev/staging stands (EC2 / IP hosts) never load any analytics. Safe to call
 * once at startup.
 *
 * Yandex.Metrika is intentionally NOT injected here: it records sessions
 * (webvisor) and must wait for explicit consent. The cookie banner calls
 * grantAnalyticsConsent once the user accepts the analytics category. GTM itself
 * may load — its tags stay dormant until the banner pushes a `consent: update`.
 */
export const initAnalytics = (): void => {
  if (isInitialized || !isProduction || !isProdHost) {
    return;
  }
  isInitialized = true;

  setConsentDefault();
  injectGtm();
};

let isYandexLoaded = false;

/**
 * Loads Yandex.Metrika once the user grants analytics consent via the cookie
 * banner. Guarded by the same production/host checks as initAnalytics, so it
 * stays a no-op on dev/staging, and by isYandexLoaded so repeated consent
 * callbacks (onConsent fires on every page load with a stored choice) inject the
 * counter only once.
 */
export const grantAnalyticsConsent = (): void => {
  if (isYandexLoaded || !isProduction || !isProdHost) {
    return;
  }
  isYandexLoaded = true;

  injectYandexMetrika();
};

/**
 * Single product-event wrapper. Replaces the previous gtag/react-ga4 calls.
 * Event list (mapped to GA inside GTM): login, sign_up, add_app, remove_app,
 * add_keyword, remove_keyword, create_order, cancel_order, first_topup,
 * tier_upgrade, balance_updated, pricing_tab_selected, user_settings_updated,
 * purchase. (pause_order is defined in the spec but intentionally not wired yet
 * — the pause action is currently hidden in the UI.)
 */
export const track = (
  event: string,
  params: Record<string, any> = {},
): void => {
  window.dataLayer?.push({ event, ...params });
};

/**
 * SPA hit for Yandex.Metrika — must fire on every route change, otherwise the
 * counter only sees the first page of the session. GA/GTM handles its own SPA
 * page_view via the History Change trigger, so we do NOT push page_view here.
 */
export const trackYandexHit = (url: string): void => {
  window.ym?.(YANDEX_COUNTER_ID, "hit", url);
};

type UserProperties = {
  spending_tier?: string;
  total_apps_added?: number;
  total_keywords_in_use?: number;
  // total_orders_active / total_orders_all_time are not exposed by the backend
  // yet (no order-totals field on the profile/account queries). They are part of
  // the type so callers can supply them once available, but are omitted today.
  total_orders_active?: number;
  total_orders_all_time?: number;
  total_spent?: number;
};

/**
 * Login identity push: user_id + user_properties in a SINGLE push together with
 * the `login` event, so the properties bind to the user (the previous approach
 * of a separate user_properties event did not bind and left GA custom
 * dimensions empty).
 *
 * Undefined/null properties are stripped so we never push empty keys. Wired from
 * the Login page after a successful sign-in (see src/pages/Login). Note: GA4
 * binds user_id for the rest of the session only if the GTM container forwards
 * it to the GA4 config tag — that mapping lives in GTM, not here.
 */
export const trackLogin = (
  method = "form",
  userId?: string,
  userProperties?: UserProperties,
): void => {
  const cleanedProps = userProperties
    ? Object.fromEntries(
        Object.entries(userProperties).filter(
          ([, value]) => value !== undefined && value !== null,
        ),
      )
    : undefined;

  window.dataLayer?.push({
    event: "login",
    method,
    ...(userId ? { user_id: userId } : {}),
    ...(cleanedProps && Object.keys(cleanedProps).length
      ? { user_properties: cleanedProps }
      : {}),
  });
};

/**
 * Purchase event on a confirmed balance top-up. Wired from the card top-up flow
 * (AddFundCardModal) when the user returns with payment_status=payment_approved.
 * currency defaults to USD.
 */
export const trackPurchase = (params: {
  transaction_id: string;
  value: number;
  currency?: string;
}): void => {
  track("purchase", {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency ?? "USD",
  });
};

